import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal, Overline, MaskLine } from "./anim";
import { FOUNDER_IMG, TESTIMONIAL_IMG } from "@/data";

const FOUNDER_NAME = "Tal Kedar";
const FOUNDER_PHOTO = "https://static.prod-images.emergentagent.com/jobs/02210e6d-97ca-4a9a-87c2-ebe863f340b9/images/3c20d0e86abeff21efd77721e7bda0e4e78b2ba29d5b5d62f28cfdeab3c39b24.jpeg";

const EXPERIENCE = [
  {
    n: "01",
    title: "Master of Counselling · PACFA Certified",
    body: "A rigorous, accredited foundation in counselling — the clinical grounding that makes it safe to take teams into genuinely honest territory.",
  },
  {
    n: "02",
    title: "Senior Facilitator, The Man Cave",
    body: "Delivered vulnerability and wellbeing workshops to over 10,000 participants, honing a facilitation style that opens rooms without ever forcing them.",
  },
  {
    n: "03",
    title: "Counsellor across schools & professional settings",
    body: "Extensive one-to-one experience supporting hundreds of individuals — including delivering Employee Assistance Program (EAP) counselling for One of Us Foundation. This is the deep listening practice behind every group I now hold.",
  },
  {
    n: "04",
    title: "Designer of culture-changing workshops",
    body: "Built and facilitated programmes for organisations including Leibler Yavneh College, Beaumaris Secondary College and Mind Over Miles Run Club.",
  },
  {
    n: "05",
    title: "Specialist work with elite young athletes",
    body: "Worked with the Victorian Institute of Sport, Sandringham Dragons, National Premier League (Soccer) and CommBank Joeys on identity development, mental wellbeing and performance enhancement.",
  },
];

const ORGANISATIONS = [
  "One of Us Foundation",
  "Just for U",
  "Deloitte",
  "Leibler Yavneh College",
  "Beaumaris Secondary College",
  "Mind Over Miles Run Club",
];

const ABOUT_STATS = [
  { k: "10k+", v: "Workshop participants" },
  { k: "100s", v: "Individuals counselled" },
  { k: "PACFA", v: "Certified practitioner" },
  { k: "Masters", v: "Of Counselling" },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "10%"]);

  const goContact = () => {
    navigate("/");
    setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 120);
  };

  return (
    <div className="bg-ink text-sand" data-testid="about-page">
      {/* Intro */}
      <section className="relative pt-36 md:pt-48 pb-20 md:pb-28 border-b border-line-d grain">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal>
            <Overline>About the founder</Overline>
          </Reveal>
          <h1 className="mt-8 font-display font-light tracking-tight leading-[1.02] text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-5xl">
            <MaskLine delay={0.15}>A career spent holding</MaskLine>
            <MaskLine delay={0.3} className="italic text-terra">the hardest conversations</MaskLine>
            <MaskLine delay={0.45}>most rooms avoid.</MaskLine>
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-10 max-w-2xl text-muted-d text-base md:text-lg leading-relaxed">
              Beyond the Mask is led by Tal Kedar — a qualified counsellor and trained mental health professional
              (Master of Counselling, PACFA Certified) and senior workshop facilitator who has spent a career helping
              people set down the mask, from elite young athletes to entire school and corporate cultures.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Portrait + story */}
      <section className="relative py-20 md:py-32 border-b border-line-d">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-8 md:gap-12">
          <div className="col-span-12 lg:col-span-5">
            <div ref={ref} className="relative overflow-hidden aspect-[4/5] bg-ink-soft">
              <motion.img style={{ y, scale: 1.12 }} src={FOUNDER_PHOTO} alt="Tal Kedar, Founder" className="h-full w-full object-cover object-center img-treatment" />
              <div className="absolute inset-0 ring-1 ring-inset ring-line-d" />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:pl-8 flex flex-col justify-center">
            <Reveal>
              <Overline>My why</Overline>
              <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.15]">
                We spend more of our lives at work than at home. So why can't we be ourselves there?
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-5 text-muted-d text-base md:text-lg leading-relaxed max-w-xl">
                <p>
                  I spent years in the corporate world and never once felt permission to be my true self. That quiet,
                  constant dissonance — being one person at work and another at home — eventually led me to leave the
                  industry altogether and begin a career in mental health.
                </p>
                <p>
                  I spent years at the award-winning <span className="text-sand">The Man Cave</span>, where I learned the
                  art of facilitation and how to create genuinely safe spaces for others — working with over 10,000
                  individuals along the way.
                </p>
                <p>
                  When I stepped away from corporate life, I realised I wasn't alone. So many people carry the same
                  weight: performing a version of themselves for eight, ten, twelve hours a day. I started Beyond the
                  Mask to change that — to give people, and the organisations they work for, permission to drop the
                  mask and finally belong in an environment where they have permission to be themselves, in the good times
                  and the bad.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-10 font-display italic text-2xl text-sand" data-testid="about-signature">
                — {FOUNDER_NAME}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Experience list */}
      <section className="relative py-20 md:py-32 border-b border-line-d">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="mb-14 md:mb-20 max-w-2xl">
            <Overline>Experience &amp; credentials</Overline>
            <h2 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight">
              A career built on trust — clinically, and in the room.
            </h2>
          </Reveal>

          <div className="divide-y divide-line-d border-y border-line-d">
            {EXPERIENCE.map((e, i) => (
              <Reveal key={e.n} delay={i * 0.05}>
                <div className="grid grid-cols-12 gap-6 py-10 md:py-12 group" data-testid={`experience-item-${i}`}>
                  <div className="col-span-12 md:col-span-2">
                    <span className="font-mono text-terra text-sm">{e.n}</span>
                  </div>
                  <div className="col-span-12 md:col-span-10 grid md:grid-cols-2 gap-5">
                    <h3 className="font-display text-2xl md:text-3xl lg:text-4xl leading-[1.1] group-hover:text-terra transition-colors duration-500">
                      {e.title}
                    </h3>
                    <p className="text-muted-d text-base md:text-lg leading-relaxed self-center max-w-md">{e.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative py-16 md:py-24 border-b border-line-d bg-ink-soft">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {ABOUT_STATS.map((s, i) => (
            <Reveal key={s.v} delay={i * 0.06}>
              <div data-testid={`about-stat-${i}`}>
                <div className="font-display text-4xl md:text-6xl text-terra leading-none">{s.k}</div>
                <div className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-d">{s.v}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Organisations */}
      <section className="relative py-20 md:py-32 border-b border-line-d">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="mb-12 max-w-xl">
            <Overline>Trusted by</Overline>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-tight">
              Organisations I have worked with.
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-line-d">
            {ORGANISATIONS.map((o, i) => (
              <Reveal key={o} delay={i * 0.04}>
                <div className="border-b border-r border-line-d p-8 md:p-10 h-full flex items-center hover:bg-ink-soft transition-colors duration-300" data-testid={`org-${i}`}>
                  <span className="font-display text-xl md:text-2xl text-sand leading-tight">{o}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-36 grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={TESTIMONIAL_IMG} alt="" className="h-full w-full object-cover img-treatment opacity-20" />
          <div className="absolute inset-0 bg-ink/80" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 text-center">
          <Reveal>
            <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] max-w-4xl mx-auto">
              Let's bring this work to your team.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <button
              onClick={goContact}
              className="group mt-12 inline-flex items-center gap-3 bg-sand text-ink px-8 py-5 font-mono text-xs tracking-[0.25em] uppercase hover:bg-terra transition-colors duration-300"
              data-testid="about-cta"
            >
              Book a 30 minute conversation
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
